<template>
  <section id="categorias" class="categorias">
    <div class="conteudo categorias__cabecalho">
      <h2 class="categorias__titulo">Categorias</h2>
      <span class="categorias__dica">arraste para ver mais</span>
    </div>

    <div class="categorias__area" :class="{ 'categorias__area--com-mais': podeAvancar }">
      <div
        ref="referencia"
        class="categorias__janela"
        :class="{ 'categorias__janela--arrastando': arrastando }"
        @scroll="atualizarMedidas"
        @pointerdown="aoPressionar"
      >
        <ul class="categorias__trilho">
          <li v-for="(item, indice) in categorias" :key="item.id">
            <button
              type="button"
              class="categoria"
              :class="{ 'categoria--ativa': estaSelecionada(item.id) }"
              :aria-pressed="estaSelecionada(item.id)"
              :style="{ '--atraso': `${indice * 45}ms` }"
              @click="selecionar(item.id)"
            >
              <span class="categoria__moldura">
                <ImagemComReserva
                  :origem="item.imagem"
                  :rotulo="item.nome"
                  cor-de-reserva="#b52220"
                  proporcao="1 / 1"
                />
                <span class="categoria__marca" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              </span>
              <span class="categoria__nome">{{ item.nome }}</span>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="conteudo">
      <div class="categorias__progresso" aria-hidden="true">
        <span :style="{ transform: `scaleX(${0.18 + progresso * 0.82})` }"></span>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { nextTick, watch } from 'vue';
  import ImagemComReserva from '@/components/marketplace/ImagemComReserva.vue';
  import usarArrasteHorizontal from '@/composables/usarArrasteHorizontal';

  const props = defineProps({
    categorias: {
      type: Array,
      default: () => []
    },
    selecionadas: {
      type: Array,
      default: () => []
    }
  });

  const emit = defineEmits(['alternar']);

  const {
    referencia,
    arrastando,
    progresso,
    podeAvancar,
    atualizarMedidas,
    aoPressionar,
    arrastouOConteudo
  } = usarArrasteHorizontal();

  function estaSelecionada(id) {
    return props.selecionadas.includes(id);
  }

  /* Arrastar o trilho não deve selecionar a categoria que ficou sob o cursor. */
  function selecionar(id) {
    if (arrastouOConteudo()) return;

    emit('alternar', id);
  }

  watch(() => props.categorias, () => nextTick(atualizarMedidas));
</script>

<style scoped>
  .categorias {
    background: var(--cor-areia);
    padding-block: var(--espaco-5);
  }

  .categorias__cabecalho {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--espaco-3);
    margin-bottom: var(--espaco-4);
  }

  .categorias__titulo {
    font-size: 1.14rem;
    font-weight: 700;
    color: var(--cor-tinta);
  }

  .categorias__dica {
    display: none;
    font-size: 0.76rem;
    color: var(--cor-texto-tenue);
  }

  .categorias__area {
    padding-inline: max(var(--espaco-4), calc((100vw - var(--largura-conteudo)) / 2 + var(--espaco-4)));
  }

  .categorias__area--com-mais {
    mask-image: linear-gradient(to right, transparent 0, #000 24px, #000 92%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 24px, #000 92%, transparent 100%);
  }

  .categorias__janela {
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    cursor: grab;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .categorias__janela::-webkit-scrollbar {
    display: none;
  }

  .categorias__janela--arrastando {
    cursor: grabbing;
    user-select: none;
  }

  .categorias__trilho {
    display: flex;
    gap: var(--espaco-3);
    list-style: none;
    margin: 0;
    padding: var(--espaco-1) 0 var(--espaco-2);
  }

  .categorias__trilho > li {
    flex: 0 0 auto;
  }

  .categoria {
    width: 118px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--espaco-2);
    padding: 0;
    animation: entrarNoTrilho 0.5s var(--atraso) var(--transicao-padrao) both;
  }

  .categoria__moldura {
    position: relative;
    display: block;
    width: 100%;
    border-radius: var(--raio-medio);
    overflow: hidden;
    box-shadow: var(--sombra-leve);
    transition: transform var(--transicao-padrao), box-shadow var(--transicao-padrao);
  }

  /*
   * Os icones sao quadrados e transparentes: `contain` sobre fundo claro os
   * mostra inteiros, sem o corte que o `cover` padrao provocava, e a folga
   * interna evita que encostem na borda.
   */
  .categoria__moldura :deep(.moldura) {
    background: var(--cor-superficie);
    animation: none;
  }

  .categoria__moldura :deep(img) {
    object-fit: contain;
    padding: 12%;
  }

  .categoria:hover .categoria__moldura {
    transform: translateY(-5px);
    box-shadow: var(--sombra-forte);
  }

  .categoria:hover .categoria__moldura :deep(img) {
    transform: scale(1.07);
  }

  .categoria__marca {
    position: absolute;
    top: var(--espaco-2);
    right: var(--espaco-2);
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--cor-marca);
    color: #fff;
    opacity: 0;
    transform: scale(0.4);
    transition: opacity var(--transicao-padrao), transform 0.34s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .categoria--ativa .categoria__moldura {
    box-shadow: 0 0 0 3px var(--cor-marca), var(--sombra-forte);
  }

  .categoria--ativa .categoria__marca {
    opacity: 1;
    transform: scale(1);
  }

  .categoria__nome {
    font-size: 0.84rem;
    font-weight: 600;
    color: var(--cor-texto-suave);
    transition: color var(--transicao-padrao);
  }

  .categoria--ativa .categoria__nome {
    color: var(--cor-marca);
  }

  .categorias__progresso {
    margin-top: var(--espaco-4);
    height: 3px;
    border-radius: 3px;
    background: rgba(43, 26, 61, 0.09);
    overflow: hidden;
  }

  .categorias__progresso span {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: var(--cor-marca);
    transform-origin: left center;
    transition: transform var(--transicao-padrao);
  }

  @keyframes entrarNoTrilho {
    from {
      opacity: 0;
      transform: translateX(22px);
    }

    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (min-width: 768px) {
    .categorias {
      padding-block: var(--espaco-6);
    }

    .categorias__dica {
      display: inline;
    }

    .categorias__titulo {
      font-size: 1.28rem;
    }

    .categorias__trilho {
      gap: var(--espaco-4);
    }

    .categoria {
      width: 152px;
    }
  }
</style>
